<template>
	<v-container>
		<v-card id="risk-description">
			<v-col>
				<h1>Risk Description : <span style="font-size: 0.8em;">{{ this.$store.getters.selectedTitle }}</span>
				</h1>
			</v-col>
			<v-col>
				<p style="font-size: 16px; margin-left: 10px; margin-right: 10px; text-align: justify;">Using this
					table, you can plot your risks into the created risk matrix, where each risk is displayed according
					to its probability and consequence. You can add, edit, or delete risks as needed. Additionally, you
					can update the status of risks following the implementation of action plans and review the
					historical record of each risk for comprehensive tracking and analysis.</p>
				<p style="font-size: 16px; margin-left: 10px; margin-right: 10px; text-align: justify;">In the table the
					risk will be defined by the level of consequence and probability you evaluate it too. Consequence
					and probability level scored as 1 in the risk definition table equals point 1-5 in the risk matrix
					and risk description table, level 2 equals 6-10, level 3 equals 11-15, level 4 equals 16-20 and
					level 5 equals 21-25. This scale from 1-25 gives you the opportunity to have several risks placed in
					the same risk level per risk matrix. We advise you to consider 1-5 probability and consequence score
					as level 1 and not to dig deeper into the scale than that. The reason we have a score of 1-25 is a
					technical interpretation of the scale 1-5.</p>
			</v-col>
			<v-col class="addnewbutton">
				<v-btn color="primary" @click="openNewRiskDialog" :disabled="isButtonDisabled">
					<v-icon left>fas fa-plus</v-icon>NEW
				</v-btn>
			</v-col>
			<v-card-title>
				<v-select
					v-model="riskFilter"
					:items="filterOptions"
					item-text="text"
					item-value="value"
					label="Filter by Risk Category"
					style="max-width: 350px; margin-right: 20px;"
					dense
					outlined
					hide-details
				>
					<template v-slot:selection="{ item }">
						{{ item.text.replace(/^- /, '') }}
					</template>
					<template v-slot:item="{ item, on, attrs }">
						<v-list-item v-bind="attrs" v-on="on">
							<v-list-item-title
								:style="item.value === 'all' || item.value.startsWith('cat:')
									? 'font-weight: bold; font-size: 16px;'
									: 'font-size: 14px;'"
							>
								{{ item.text }}
							</v-list-item-title>
						</v-list-item>
					</template>
				</v-select>
				<v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line
					hide-details></v-text-field>
			</v-card-title>
			<v-data-table theme="--light" :headers="headers" :items="filteredConnections" :search="search" :items-per-page="20"
				class="light table" :footer-props="{
					'items-per-page-options': [5, 10, 15, 20],
				}">
				<template v-slot:header="{ headers }">
					<thead>
						<tr>
							<th v-for="header in headers" :key="header.text" class="custom-header"
								style="text-align: left;">
								{{ header.text }}
							</th>
						</tr>
					</thead>
				</template>
				<template v-slot:item.Risk="{ item }">
					<td style="text-align: center;">
						<span style="text-align: center;">{{ item.Risk }}</span>
					</td>
				</template>
				<template v-slot:item.RiskDescription="{ item }">
					<td style="text-align: justify;">
						<span>{{ item.RiskDescription }}</span>
					</td>
				</template>
				<template v-slot:item.TypesOfRisk="{ item }">
					<td style="text-align: center;">
						<span>{{ item.TypesOfRisk }}</span>
					</td>
				</template>
				<template v-slot:item.RiskOwner="{ item }">
					<td style="text-align: center;">
						<span>{{ item.RiskOwner }}</span>
					</td>
				</template>
				<template v-slot:item.Created="{ item }">
					<td style="text-align: center;">
						<span>{{ item.Created }}</span>
					</td>
				</template>
				<template v-slot:item.Creator="{ item }">
					<td style="text-align: center;">
						<span>{{ item.Creator }}</span>
					</td>
				</template>
				<template v-slot:item.Consequence="{ item }">
					<td style="text-align: center;">
						<span
							class="risk-status-badge"
							:class="{
								'status-low': calculateStatus(item.Probability, item.Consequence) === 'Low',
								'status-moderate': calculateStatus(item.Probability, item.Consequence) === 'Moderate',
								'status-high': calculateStatus(item.Probability, item.Consequence) === 'High',
							}"
						>{{ item.Consequence }},{{ item.Probability }}</span>
					</td>
				</template>
				<template v-slot:item.ApprovedBy="{ item }">
					<td style="text-align: center;">
						<span v-if="isApproved(item.ApprovedBy)">Approved by: {{ item.ApprovedBy }}</span>
						<v-btn
							v-else
							small
							color="success"
							:loading="item.approving"
							@click="approveItem(item)"
						>
							Approve
						</v-btn>
					</td>
				</template>
				<template v-slot:item.editbutton="{ item }">
					<td class="small_cell" style="width: 30px; padding-right: 0px">
						<v-menu offset-y>
							<template v-slot:activator="{ on, attrs }">
								<div class="buttonfeather">
									<v-btn v-bind="attrs" v-on="on" small elevation="0" text>
										<v-icon>fas fa-ellipsis-v</v-icon>
									</v-btn>
								</div>
							</template>
							<v-list>
								<v-list-item @click="editItem(item)" :disabled="!isActionAllowed">
									<v-list-item-title>Edit</v-list-item-title>
								</v-list-item>
								<v-list-item @click="deleteItem(item)">
									<v-list-item-title>Delete</v-list-item-title>
								</v-list-item>
								<v-list-item @click="updateItem(item)" :disabled="!isActionAllowed">
									<v-list-item-title>Update Status</v-list-item-title>
								</v-list-item>
								<v-list-item @click="historyItem(item)">
									<v-list-item-title> History</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</td>
				</template>
			</v-data-table>
			<v-dialog v-model="dialogDelete" max-width="500px">
				<v-card>
					<v-card-title>Are you sure you want to remove this Risk?</v-card-title>
					<v-card-text>Once it is removed, you will not able to track your
						risk</v-card-text>
					<v-card-actions>
						<v-btn color="primary" text @click="dialogDelete = false">Close</v-btn>
						<v-btn color="error" text @click="removeRow">Delete</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
			<v-dialog v-model="dialogHistory" max-width="1000px">
				<v-card>
					<div style="text-align: right;">
						<v-btn color="red" text @click="dialogHistory = false"><v-icon>fa-solid
								fa-rectangle-xmark</v-icon></v-btn>
					</div>
					<v-col align="center">
						<h2>History of the risk updates</h2>
						<p><i>This is a log of status changes made to this risk.</i></p>
					</v-col>
					<v-col justify-center cols="12">
						<v-data-table hide-default-footer theme-- light :headers="statusHeaders" :items="tableData"
							class="light table mx-4 completed-table" :footer-props="{
								'items-per-page-options': [5, 10, 15],
							}">
							<template v-slot:item.Status="{ item }">
								<span :class="{
										'green--text': item.Status === 'Low',
										'red--text': item.Status === 'High',
										'yellow--text': item.Status === 'Moderate',
									}">{{ item.Status }}</span>
							</template>
							<template v-slot:item.Timestamp="{ item }">
								<span>{{ item.DisplayTimestamp }}</span>
							</template>
							<template v-slot:item.Consequence="{ item }">
								<span>{{ item.IsStart ? 'Start: ' + item.Consequence : item.Consequence }}</span>
							</template>
							<template v-slot:item.Probability="{ item }">
								<span>{{ item.IsStart ? 'Start: ' + item.Probability : item.Probability }}</span>
							</template>
						</v-data-table>
					</v-col>
					<br />
				</v-card>
			</v-dialog>
			<v-dialog v-model="dialogNewRisk" max-width="1200px">
				<v-card style="padding: 20px;">
					<div style="text-align: right;">
						<v-btn color="red" text @click="dialogNewRisk = false"><v-icon>fa-solid
								fa-rectangle-xmark</v-icon></v-btn>
					</div>
					<v-card-title>
						Select Risk Type
						<v-spacer></v-spacer>
						<v-btn icon @click="dialogNewRisk = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>

					<v-stepper v-model="riskStep" non-linear>
						<v-stepper-header>
							<template v-for="(cat, index) in riskCategories">
								<v-stepper-step
									:key="'step-' + cat.id"
									:step="cat.id"
									editable
								>
									{{ cat.title }}
								</v-stepper-step>
								<v-divider
									v-if="index < riskCategories.length - 1"
									:key="'divider-' + cat.id"
								></v-divider>
							</template>
						</v-stepper-header>

						<v-stepper-items>
							<v-stepper-content
								v-for="cat in riskCategories"
								:key="'content-' + cat.id"
								:step="cat.id"
							>
								<v-list>
									<v-list-item
										v-for="(sub, idx) in cat.subcategories"
										:key="idx"
										class="risk-type-item"
										@click="selectRiskType(cat.title, sub.name)"
									>
										<v-list-item-content>
											<v-list-item-title>{{ sub.name }}</v-list-item-title>
											<v-list-item-subtitle class="risk-type-description">
												{{ sub.description }}
											</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-stepper-content>
						</v-stepper-items>
					</v-stepper>
				</v-card>
			</v-dialog>
		</v-card>
	</v-container>
</template>

<script>
import { riskAPI } from "../api/csr/risk"
import LoadingState from "../consts/LoadingState"
import { mapGetters } from "vuex"
import { riskmatrixAPI } from "../api/csr/riskmatrix"

export default {
	data() {
		return {
			ID: null,
			clientID: null,
			dialogDelete: false,
			dialogHistory: false,
			connections: [],
			avgScore: [],
			search: "",
			categorySubCategories: {
				"Governance & Management": [
					"Corruption",
					"Country",
					"Financial",
					"Quality Assurance Management",
					"Management",
				],
				"People & Human Rights": [
					"Gender Rights",
					"Human Rights",
					"Labour",
				],
				"Environment & Resources": [
					"Environment",
					"Technology",
					"Water Use",
				],
				"Operations & Supply Chains": [
					"Market",
					"Production",
					"Quality",
					"Supply Chains",
				],
			},
			headers: [
				{
					text: "No",
					align: "start",
					value: "no",
					sortable: false,
					width:"5%",
				},
				{
					text: "Risk",
					align: "center",
					value: "Risk",
					sortable: false,
					width:"5%",
				},
				{
					text: "Risk Description",
					align: "center",
					value: "RiskDescription",
					sortable: false,
					width:"40%",
				},
				{
					text: "Risk Type",
					align: "center",
					value: "TypesOfRisk",
					sortable: false,
					width:"15%",
					
				},
				{
					text: "Risk Owner",
					value: "RiskOwner",
					sortable: false,
				},
				{
					text: "Created",
					value: "Created",
					sortable: false,
					width:"12%",

				},
				{
					text: "Creator",
					align: "center",
					value: "Creator",
					sortable: false,
				},
				{
					text: "Cons,Prob",
					value: "Consequence",
					sortable: false,
				},
				{
					text: "Approval",
					value: "ApprovedBy",
					sortable: false,
				},
				{
					value: "editbutton",
					sortable: false,
				},
			],
			statusHeaders: [
				{
					text: "Creator",
					value: "Creator",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Status",
					value: "Status",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Timestamp",
					value: "Timestamp",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Comment",
					value: "Comment",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Consequence",
					value: "Consequence",
					sortable: false,
					class: "secondary--text",
				},
				{
					text: "Probability",
					value: "Probability",
					sortable: false,
					class: "secondary--text",
				},
			],

			tableData: [],
			dialogNewRisk: false,
			riskStep: 1,
			riskCategories: [
				{
					id: 1,
					title: "Governance & Management",
					subcategories: [
						{ name: "Management", description: "Risks related to organizational leadership, decision-making structures, and internal oversight." },
						{ name: "Quality Assurance Management", description: "Risks related to processes ensuring products/services consistently meet quality standards." },
						{ name: "Financial", description: "Risks related to financial stability, reporting accuracy, budgeting, and fiscal controls." },
						{ name: "Corruption", description: "Risks related to bribery, fraud, unethical conduct, and lack of anti-corruption controls." },
						{ name: "Country", description: "Risks arising from the political, legal, or economic environment of the country of operation." },
					],
				},
				{
					id: 2,
					title: "People & Human Rights",
					subcategories: [
						{ name: "Labour", description: "Risks related to working conditions, fair wages, working hours, and labour practices." },
						{ name: "Human Rights", description: "Risks related to violations of fundamental human rights across operations and supply chains." },
						{ name: "Gender Rights", description: "Risks related to gender equality, discrimination, and equal opportunity in the workplace." },
					],
				},
				{
					id: 3,
					title: "Environment & Resources",
					subcategories: [
						{ name: "Environment", description: "Risks related to environmental impact, emissions, waste management, and regulatory compliance." },
						{ name: "Water Use", description: "Risks related to water consumption, scarcity, and responsible water management." },
						{ name: "Technology", description: "Risks related to reliance on outdated systems, cybersecurity, or lack of technological resilience." },
					],
				},
				{
					id: 4,
					title: "Operations & Supply Chains",
					subcategories: [
						{ name: "Quality", description: "Risks related to product or service quality control and consistency." },
						{ name: "Production", description: "Risks related to manufacturing processes, capacity, and operational continuity." },
						{ name: "Market", description: "Risks related to market volatility, competition, and demand fluctuations." },
						{ name: "Supply Chains", description: "Risks related to supplier reliability, traceability, and supply chain disruptions." },
					],
				},
			],
			riskFilter: "all",
		}
	},
	computed: {
		...mapGetters([
			"selectedId",
			"selectedYear",
			"selectedTitle",
			"selectedDescription",
			"selectedStatus",
			"selectedCategory",
			"selectedSubCategory",
		]),
		isActionAllowed() {
			return this.selectedStatus === "Active"
		},
		isButtonDisabled() {
			return !this.isActionAllowed || this.selectedId === null
		},
		filteredConnections() {
			if (this.riskFilter === "all") {
				return this.connections
			}

			if (this.riskFilter.startsWith("sub:")) {
				const sub = this.riskFilter.replace("sub:", "")
				return this.connections.filter(
					(item) => (item.TypesOfRisk || "").trim().toLowerCase() === sub.trim().toLowerCase()
				)
			}

			if (this.riskFilter.startsWith("cat:")) {
				const category = this.riskFilter.replace("cat:", "")
				const subs = this.categorySubCategories[category] || []
				return this.connections.filter((item) => {
					const type = (item.TypesOfRisk || "").trim().toLowerCase()
					return subs.some((s) => s.trim().toLowerCase() === type)
				})
			}

			return this.connections
		},
		filterOptions() {
			let options = [{ text: "All", value: "all" }]

			for (let category in this.categorySubCategories) {
				options.push({ text: category, value: "cat:" + category })
				for (let sub of this.categorySubCategories[category]) {
					options.push({ text: "- " + sub, value: "sub:" + sub })
				}
			}

			return options
		},
	},
	watch: {
		selectedId(newVal) {
			console.log("Selected ID changed Risk Description:", newVal)
			this.loadItems(newVal)
			if(newVal==null){
				this.isActionAllowed()
			}
		},
	},
	mounted() {
		this.loadItems(this.selectedId)
		this.approverisk
	},
	methods: {
		isApproved(value) {
			return value !== null && value !== undefined && String(value).trim().toUpperCase() !== "NULL" && String(value).trim() !== ""
		},
		loadItems(id) {
			this.connections = []
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			riskAPI
				.getAll(id)
				.then((data) => {
					let editbutton = ""
					let no = 0

					for (let entry of data[0]) {
						this.connections.push({
							no: ++no,
							ID: entry.ID,
							Risk: entry.Risk,
							RiskDescription: entry.RiskDescription,
							TypesOfRisk: entry.TypesOfRisk,
							RiskOwner: entry.RiskOwner,
							Created: entry.Created.split("T")[0],
							Creator: entry.Creator,
							Consequence:
								entry.UpdatedConsequence != null
									? entry.UpdatedConsequence
									: entry.Consequence,
							Probability:
								entry.UpdatedProbability != null
									? entry.UpdatedProbability
									: entry.Probability,
							editbutton: editbutton,
							FirstConsequence: entry.Consequence,
							FirstProbability: entry.Probability,
							UpdatedConsequence: entry.UpdatedConsequence,
							UpdatedProbability: entry.UpdatedProbability,
							UpdatedDate:
								entry.UpdatedDate != null
									? entry.UpdatedDate.split("T")[0]
									: entry.UpdatedDate,
							CommentforUpdate: entry.CommentforUpdate,
							ApprovedBy:
								entry.ApprovedBy != null && String(entry.ApprovedBy).trim().toUpperCase() !== "NULL"
									? entry.ApprovedBy
									: null,
						})
					}
	
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		calculateStatus(probability, consequence) {
			if (
				(probability <= 5 && consequence <= 20) ||
				(probability <= 10 && consequence <= 10) ||
				(probability <= 20 && consequence <= 5)
			) {
				return "Low"
			} else if (
				(probability > 20 && consequence > 10) ||
				(probability > 15 && consequence > 15) ||
				(probability > 10 && consequence > 20)
			) {
				return "High"
			} else {
				return "Moderate"
			}
		},
		deleteItem(item) {
			this.itemToDelete = item
			this.dialogDelete = !this.dialogDelete
		},
		async historyItem(item) {
			this.itemToHistory = item
			this.dialogHistory = !this.dialogHistory

			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			try {
				let historyData = await riskAPI.getRiskHistory(item.ID)
				let historyRows = historyData[0] || []

				let mapped = historyRows.map((h) => ({
					Creator: item.Creator,
					Status: this.calculateStatus(h.Probability, h.Consequence),
					Timestamp: h.Created,
					DisplayTimestamp: h.Created ? h.Created.split("T")[0] : h.Created,
					Comment: h.Comment,
					Consequence: h.Consequence,
					Probability: h.Probability,
					IsStart: false,
				}))

				mapped.sort((a, b) => new Date(a.Timestamp) - new Date(b.Timestamp))
				mapped = mapped.slice(-4)

				let startRow = {
					Creator: item.Creator,
					Status: this.calculateStatus(item.FirstProbability, item.FirstConsequence),
					Timestamp: item.Created,
					DisplayTimestamp: item.Created ? item.Created.split("T")[0] : item.Created,
					Comment: item.RiskDescription,
					Consequence: item.FirstConsequence,
					Probability: item.FirstProbability,
					IsStart: true,
				}

				this.$set(this, "tableData", [startRow, ...mapped])

				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_END_LOADING,
					null,
					{ root: true }
				)
			} catch (error) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					error,
					{ root: true }
				)
			}
		},

		updateItem(item) {
			this.$router.push({
				path: "/risk/update",
				query: {
					itemId: item.ID,
				},
			})
		},
		editItem(item) {
			this.$router.push({
				path: "/risk/edit",
				query: {
					itemId: item.ID,
				},
			})
		},
		removeRow() {
			this.dialogDelete = false
			let index = this.connections.indexOf(this.itemToDelete)
			this.$store.commit("setSelectedNewstate", true)
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			riskAPI
				.deleteRisk(this.itemToDelete.ID)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Risk item was successfully removed from the list.",
						{ root: true }
					)
					
					this.connections.splice(index, 1)
					window.location.href = "/risk/riskpage"
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
			
		},
		openNewRiskDialog() {
			this.riskStep = 1
			this.dialogNewRisk = true
		},
		selectRiskType(category, subcategory) {
			this.dialogNewRisk = false
			this.$router.push({
				path: "/risk/add",
				query: {
					matrixID: this.selectedId,
					riskType: subcategory,
				},
			})
		},
		async approveItem(item) {
			this.$set(item, "approving", true)
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			try {
				let result = await riskmatrixAPI.approveRisk(item.ID)
				this.$set(item, "ApprovedBy", result.Approve)
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_END_LOADING,
					null,
					{ root: true }
				)
			} catch (error) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					error,
					{ root: true }
				)
			} finally {
				this.$set(item, "approving", false)
			}
		},
	},
}
</script>

<style>
.v-progress-circular {
	margin: 0rem;
}

.addnewbutton {
	display: flex;
	justify-content: flex-end;
}

.custom-header {
	font-size: 100px;
}

.v-data-table .v-data-table-header tr th {
	font-size: 15px !important;
	color: #217b7f !important;
}

.buttonfeather {
	display: flex;
}

.small_cell {
	width: 20px !important;
	padding-right: 0px;
}

.risk-type-item {
	background-color: #f0f0f0;
	margin-bottom: 6px;
	border-radius: 2px;
}

.risk-type-item:hover {
	background-color: #e0e0e0;
	cursor: pointer;
}

.risk-status-badge {
	display: inline-block;
	padding: 4px 10px;
	border-radius: 4px;
	font-weight: bold;
}

.status-low {
	background-color: #2e7d32;
	color: #fff;
}

.status-moderate {
	background-color: #f5f043;
	color: #000;
}

.status-high {
	background-color: #e53935;
	color: #fff;
}
</style>
